/**
 * usePhone Hook — Twilio Voice SDK Device Management
 * 
 * Manages the Twilio Voice Device lifecycle, incoming/outgoing calls,
 * and call state. Used by PhoneWidget for the browser softphone.
 * 
 * @module hooks/usePhone
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { voiceApi } from '../stores/api';
import { useAuthStore } from '../stores/authStore';

const PHONE_STATUS = {
    OFFLINE: 'offline',
    CONNECTING: 'connecting',
    READY: 'ready',
    RINGING: 'ringing',
    IN_CALL: 'in-call',
    ERROR: 'error'
};

export function usePhone() {
    const [status, setStatus] = useState(PHONE_STATUS.OFFLINE);
    const [error, setError] = useState(null);
    const [incomingCall, setIncomingCall] = useState(null);
    const [activeCall, setActiveCall] = useState(null);
    const [isMuted, setIsMuted] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [callerInfo, setCallerInfo] = useState(null);
    const [isAvailable, setIsAvailable] = useState(false);

    const { token: authToken } = useAuthStore();
    const deviceRef = useRef(null);
    const timerRef = useRef(null);
    const callStartRef = useRef(null);

    // ─── Check if voice calling is available ────────────────
    useEffect(() => {
        if (!authToken) return;
        
        voiceApi.getStatus()
            .then(data => setIsAvailable(data.available))
            .catch(() => setIsAvailable(false));
    }, [authToken]);

    // ─── Initialize Device ──────────────────────────────────
    const initialize = useCallback(async () => {
        if (deviceRef.current) {
            deviceRef.current.destroy();
        }

        setStatus(PHONE_STATUS.CONNECTING);
        setError(null);

        try {
            const { token } = await voiceApi.getToken();

            const device = new Device(token, {
                edge: 'dublin',
                logLevel: 1, // errors only
                codecPreferences: ['opus', 'pcmu']
            });

            // ─── Device Events ──────────────────────────
            device.on('registered', () => {
                console.log('📞 Phone ready');
                setStatus(PHONE_STATUS.READY);
            });

            device.on('unregistered', () => {
                console.log('📞 Phone offline');
                setStatus(PHONE_STATUS.OFFLINE);
            });

            device.on('error', (err) => {
                console.error('📞 Phone error:', err);
                setError(err.message || 'Phone error');
                setStatus(PHONE_STATUS.ERROR);
            });

            device.on('tokenWillExpire', async () => {
                console.log('🔄 Refreshing voice token...');
                try {
                    const { token: newToken } = await voiceApi.getToken();
                    device.updateToken(newToken);
                } catch (e) {
                    console.error('❌ Token refresh failed:', e);
                }
            });

            device.on('incoming', (call) => {
                console.log('📞 Incoming call from:', call.parameters.From);
                setIncomingCall(call);
                setCallerInfo({
                    number: call.parameters.From || 'Unknown',
                    direction: 'inbound'
                });
                setStatus(PHONE_STATUS.RINGING);

                call.on('cancel', () => {
                    console.log('📞 Incoming call cancelled');
                    setIncomingCall(null);
                    setCallerInfo(null);
                    setStatus(PHONE_STATUS.READY);
                });

                call.on('disconnect', () => {
                    handleCallEnd();
                });

                call.on('reject', () => {
                    setIncomingCall(null);
                    setCallerInfo(null);
                    setStatus(PHONE_STATUS.READY);
                });
            });

            await device.register();
            deviceRef.current = device;
        } catch (err) {
            console.error('❌ Phone init failed:', err);
            setError(err.message || 'Failed to initialize phone');
            setStatus(PHONE_STATUS.ERROR);
        }
    }, []);

    // ─── Call Duration Timer ────────────────────────────────
    const startTimer = useCallback(() => {
        callStartRef.current = Date.now();
        setCallDuration(0);
        timerRef.current = setInterval(() => {
            setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
        }, 1000);
    }, []);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        callStartRef.current = null;
    }, []);

    // ─── Call End Handler ───────────────────────────────────
    const handleCallEnd = useCallback(() => {
        stopTimer();
        setActiveCall(null);
        setIncomingCall(null);
        setCallerInfo(null);
        setIsMuted(false);
        setCallDuration(0);
        setStatus(PHONE_STATUS.READY);
    }, [stopTimer]);

    // ─── Make Outbound Call ─────────────────────────────────
    const makeCall = useCallback(async (number) => {
        if (!deviceRef.current || status !== PHONE_STATUS.READY) {
            throw new Error('Phone not ready');
        }

        try {
            const call = await deviceRef.current.connect({
                params: { To: number }
            });

            setActiveCall(call);
            setCallerInfo({ number, direction: 'outbound' });
            setStatus(PHONE_STATUS.IN_CALL);
            startTimer();

            call.on('disconnect', () => handleCallEnd());
            call.on('cancel', () => handleCallEnd());
            call.on('error', (err) => {
                console.error('📞 Call error:', err);
                handleCallEnd();
            });

            return call;
        } catch (err) {
            console.error('❌ Outbound call failed:', err);
            throw err;
        }
    }, [status, startTimer, handleCallEnd]);

    // ─── Answer Incoming Call ───────────────────────────────
    const answerCall = useCallback(() => {
        if (!incomingCall) return;

        incomingCall.accept();
        setActiveCall(incomingCall);
        setIncomingCall(null);
        setStatus(PHONE_STATUS.IN_CALL);
        startTimer();
    }, [incomingCall, startTimer]);

    // ─── Reject Incoming Call ───────────────────────────────
    const rejectCall = useCallback(() => {
        if (!incomingCall) return;

        incomingCall.reject();
        setIncomingCall(null);
        setCallerInfo(null);
        setStatus(PHONE_STATUS.READY);
    }, [incomingCall]);

    // ─── Hang Up Active Call ────────────────────────────────
    const hangUp = useCallback(() => {
        if (activeCall) {
            activeCall.disconnect();
        }
        handleCallEnd();
    }, [activeCall, handleCallEnd]);

    // ─── Toggle Mute ────────────────────────────────────────
    const toggleMute = useCallback(() => {
        if (!activeCall) return;

        const newMuted = !isMuted;
        activeCall.mute(newMuted);
        setIsMuted(newMuted);
    }, [activeCall, isMuted]);

    // ─── Send DTMF Digits ───────────────────────────────────
    const sendDigits = useCallback((digits) => {
        if (!activeCall) return;
        activeCall.sendDigits(digits);
    }, [activeCall]);

    // ─── Disconnect Device ──────────────────────────────────
    const disconnect = useCallback(() => {
        if (deviceRef.current) {
            deviceRef.current.destroy();
            deviceRef.current = null;
        }
        handleCallEnd();
        setStatus(PHONE_STATUS.OFFLINE);
    }, [handleCallEnd]);

    // ─── Cleanup on unmount ─────────────────────────────────
    useEffect(() => {
        return () => {
            stopTimer();
            if (deviceRef.current) {
                deviceRef.current.destroy();
            }
        };
    }, [stopTimer]);

    return {
        // State
        status,
        error,
        isAvailable,
        incomingCall: !!incomingCall,
        activeCall: !!activeCall,
        isMuted,
        callDuration,
        callerInfo,

        // Actions
        initialize,
        makeCall,
        answerCall,
        rejectCall,
        hangUp,
        toggleMute,
        sendDigits,
        disconnect,

        // Constants
        PHONE_STATUS
    };
}

export { PHONE_STATUS };
export default usePhone;
